import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BookOpenIcon, MagnifyingGlassIcon, WarningIcon, CheckCircleIcon,
  LinkSimpleIcon, StarIcon, PlusIcon, CheckIcon, ArrowSquareOutIcon, CopyIcon, ShieldWarningIcon,
} from "@phosphor-icons/react";
import { useTrustedCreators } from "../lib/useTrustedCreators";
import { shortAddr, isValidSolanaAddress } from "../lib/solana";
import { fetchHiddenWallets, submitRemovalRequest } from "../lib/api";

const TOOLS = [
  { name: "GMGN.ai", url: "https://gmgn.ai", note: "Smart-money tracking, dev holdings %, insider/bundle detection, PnL leaderboards. Top pick for meme coins." },
  { name: "Solscan", url: "https://solscan.io", note: "Inspect a token's creation tx to find the original deployer wallet; view holders + tx history." },
  { name: "SolanaFM", url: "https://solana.fm", note: "Alternative explorer — labeled accounts and clean transfer graphs." },
  { name: "KOLscan", url: "https://kolscan.io", note: "Realtime leaderboard of top memecoin traders / KOL wallets with PnL." },
  { name: "Dune — Solana KOL Wallets", url: "https://dune.com", note: "Community-maintained dashboard of ~30+ influencer wallets with names + handles." },
  { name: "DexScreener", url: "https://dexscreener.com", note: "Live charts, liquidity, volume and basic security data per pair." },
  { name: "Birdeye", url: "https://birdeye.so", note: "Charts + holder/security snapshots for any Solana token." },
  { name: "Bubblemaps", url: "https://bubblemaps.io", note: "Visualize holder clusters — spot bundled/connected wallets at a glance." },
  { name: "RugCheck", url: "https://rugcheck.xyz", note: "Mint/freeze authority, LP status, top-holder concentration, risk score." },
  { name: "Axiom", url: "https://axiom.trade", note: "Fast trading terminal with wallet trackers + insider/bundle filters." },
  { name: "Nansen", url: "https://nansen.ai", note: "Labeled 'smart money' analysis (paid tiers) for deeper wallet intel." },
];

// Publicly-discussed wallets surfaced from community analyses / Dune KOL list.
// These are trader / KOL wallets, NOT confirmed original deployers. Always verify recent activity.
const CURATED = [
  { group: "Smart Money & high-PnL wallets", tag: "smart", wallets: [
    { name: "ckwifhatday", handle: "WIF gains", addr: "EdCNh8EzETJLFphW8yvdY7rDd8zBiyweiz8DU5gUUUka" },
    { name: "popchad", handle: "POPCAT gains", addr: "8mZYBV8aPvPCo34CyCmt6fWkZRFviAUoBZr1Bn993gro" },
    { name: "SmartMoney_Alpha1", handle: "high win-rate Pump.fun", addr: "AVAZvHLR2PcWpDf8BXY4rVxNHYRBytycHkcB5z5QNXYm" },
    { name: "SmartMoney_Alpha2", handle: "50x+ Raydium flips", addr: "4Be9CvxqHW6BYiRAxW9Q3xu1ycTMWaL5z8NX4HR3ha7t" },
    { name: "SmartMoney_Alpha3", handle: "insider signal tracking", addr: "8zFZHuSRuDpuAR7J6FzwyF3vKNx4CVW3DFHJerQhc7Zd" },
    { name: "SmartMoney_Alpha4", handle: "whale, clean track record", addr: "H72yLkhTnoBfhBTXXaj1RBXuirm8s8G5fcVh2XpQLggM" },
  ]},
  { group: "Influencer / KOL wallets", tag: "kol", wallets: [
    { name: "Ansem", handle: "@blknoiz06", addr: "AVAZvHLR2PcWpDf8BXY4rVxNHYRBytycHkcB5z5QNXYm" },
    { name: "Ansem (holder)", handle: "$ANSEM big holder", addr: "GV6UUmNxz2RpKxmNAPadYKb7uQpszwqQAu3qLJxVdC52" },
    { name: "Bastille", handle: "@BastilleBtc", addr: "3kebnKw7cPdSkLRfiMEALyZJGZ4wdiSRvmoN4rD1yPzV" },
    { name: "Pow", handle: "@traderpow", addr: "8zFZHuSRuDpuAR7J6FzwyF3vKNx4CVW3DFHJerQhc7Zd" },
    { name: "Gake", handle: "@Ga__ke", addr: "DNfuF1L62WWyW3pNakVkyGGFzVVhj4Yr52jSmdTyeBHm" },
    { name: "Cooker", handle: "@CookerFlips", addr: "8deJ9xeUvXSJwicYptA9mHsU2rN2pDx37KWzkDkEXhU6" },
    { name: "Brox", handle: "@ohbrox", addr: "7VBTpiiEjkwRbRGHJFUz6o5fWuhPFtAmy8JGhNqwHNnn" },
    { name: "Nach", handle: "@NachSOL", addr: "ATKi3ZvMbo31pbgBgGSGQPDPKEbQ4oGzoDrwG2sms56k" },
    { name: "Tobx", handle: "@TobxG", addr: "HmBmSYwYEgEZuBUYuDs9xofyqBAkw4ywugB1d7R7sTGh" },
    { name: "OGAntD", handle: "@0GAntD", addr: "215nhcAHjQQGgwpQSJQ7zR26etbjjtVdW74NLzwEgQjP" },
    { name: "ChartFu", handle: "@ChartFuMonkey", addr: "7i7vHEv87bs135DuoJVKe9c7abentawA5ydfWcWc8iY2" },
    { name: "Zyaf", handle: "@0xZyaf", addr: "F5TjPySiUJMdvqMZHnPP85Rc1vErDGV5FR5P2vdVm429" },
    { name: "Shocked JS", handle: "@ShockedJS", addr: "6m5sW6EAPAHncxnzapi1ZVJNRb9RZHQ3Bj7FD84X9rAF" },
    { name: "Gorilla Capital", handle: "@gorillacapsol", addr: "DpNVrtA3ERfKzX4F8Pi2CVykdJJjoNxyY5QgoytAwD26" },
    { name: "Insentos", handle: "@insentos", addr: "7SDs3PjT2mswKQ7Zo4FTucn9gJdtuW4jaacPA65BseHS" },
    { name: "Cuh", handle: "@quarsays", addr: "9AqzsYXj1M2z8shG6resmM7LNM6GdvsjcjhjRUPc1dNf" },
    { name: "Levis", handle: "@LevisNFT", addr: "GwoFJFjUTUSWq2EwTz4P2Sznoq9XYLrf8t4q5kbTgZ1R" },
    { name: "Duke", handle: "@dukezfn", addr: "DeVjHYTEZEi7Wvcvfjz8KZMzpuZpijABgutSfXn1BxjX" },
  ]},
];

const RED_FLAGS = [
  "High % of supply held by the dev + aggressive selling shortly after launch.",
  "Clustered “bundled” buys from many fresh wallets at launch (coordinated sniping / fake volume).",
  "LP not locked or burned; mint authority still active.",
  "Heavy concentration in the top 10 holders.",
];
const GREEN_SIGNALS = [
  "Low dev allocation (<5–10%), transparently communicated.",
  "LP locked or burned.",
  "Contract renounced (mint + freeze authority revoked).",
  "Dev wallet inactive or used for buybacks/burns from creator fees.",
];
const FIND_STEPS = [
  "Open the token on Solscan and find its creation / launch transaction — the fee-payer is usually the original deployer wallet.",
  "Paste the CA into GMGN.ai to read dev %, top-holder concentration, and whether smart money is accumulating.",
  "Check the deployer's history: has it launched other coins? Did those rug or run?",
  "Name the dev in AlphaApeing and add every wallet they rotate through — a launch from ANY of them pings you early.",
];

const WalletRow = ({ w, groupName, isAlpha, onAdd }) => {
  const valid = isValidSolanaAddress(w.addr);
  const added = isAlpha(w.addr);
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!valid) return;
    setBusy(true);
    try { await onAdd(w.name, w.addr); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(w.addr); toast.success("Address copied"); };
  return (
    <div data-testid={`playbook-wallet-${w.addr}`} className="flex items-center gap-3 border border-white/10 bg-black/30 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-sm truncate">{w.name}</span>
          <span className="font-mono text-[10px] text-muted-foreground truncate">{w.handle}</span>
        </div>
        <button onClick={copy} title="Copy address" className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-white transition-colors">
          {shortAddr(w.addr)} <CopyIcon size={11} />
        </button>
      </div>
      <a href={`https://gmgn.ai/sol/address/${w.addr}`} target="_blank" rel="noreferrer" title="View on GMGN" className="p-1.5 text-muted-foreground hover:text-white transition-colors">
        <ArrowSquareOutIcon size={15} />
      </a>
      {added ? (
        <span className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-[#C9A227]/40 text-[#F0C000] whitespace-nowrap"><CheckIcon size={13} weight="bold" /> On Alpha</span>
      ) : (
        <button
          data-testid={`playbook-add-${w.addr}`}
          onClick={add}
          disabled={busy || !valid}
          title={valid ? "Add to Alpha Devs" : "Invalid address"}
          className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 bg-[#F0C000] text-black font-semibold hover:bg-[#F0C000]/90 transition-colors disabled:opacity-40 whitespace-nowrap"
        >
          <PlusIcon size={13} weight="bold" /> {busy ? "Adding…" : "Add"}
        </button>
      )}
    </div>
  );
};

const RemovalForm = () => {
  const [wallet, setWallet] = useState("");
  const [handle, setHandle] = useState("");
  const [reason, setReason] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    const w = wallet.trim();
    if (!isValidSolanaAddress(w)) { toast.error("Enter a valid Solana wallet address."); return; }
    setBusy(true);
    try {
      await submitRemovalRequest({ wallet: w, handle: handle.trim(), reason: reason.trim(), contact: contact.trim() });
      setDone(true);
      toast.success("Request received — this wallet is now hidden pending review.");
    } catch {
      toast.error("Couldn't submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section data-testid="removal-form" className="border border-neon/30 bg-neon/[0.05] p-5 flex items-start gap-3">
        <CheckCircleIcon size={20} weight="fill" className="text-neon shrink-0 mt-0.5" />
        <div>
          <div className="font-heading font-bold text-sm">Removal request received</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">This wallet has been hidden from the Playbook immediately. We'll review it shortly. Thank you.</p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="removal-form" className="border border-white/10 bg-card p-5 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldWarningIcon size={18} weight="fill" className="text-white" />
        <h3 className="font-heading font-bold text-base">Request removal</h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Own one of these wallets, or want your name/handle untagged? Submit the wallet below — it's
        <span className="text-white"> hidden from this list immediately</span> upon request, pending review.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="removal-wallet-input" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="Wallet address *" className="bg-black/40 border border-white/10 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-neon/50" />
        <input data-testid="removal-handle-input" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="Your name / X handle (optional)" className="bg-black/40 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-neon/50" />
        <input data-testid="removal-contact-input" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact email or handle (optional)" className="bg-black/40 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-neon/50 sm:col-span-2" />
        <textarea data-testid="removal-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" rows={2} className="bg-black/40 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-neon/50 sm:col-span-2 resize-none" />
      </div>
      <button data-testid="removal-submit-btn" onClick={submit} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-40">
        {busy ? "Submitting…" : "Submit removal request"}
      </button>
    </section>
  );
};

export const CreatorPlaybook = () => {
  const { isAlpha, addWallet } = useTrustedCreators();
  const { data: hiddenData } = useQuery({ queryKey: ["hiddenWallets"], queryFn: fetchHiddenWallets, staleTime: 300000 });
  const hidden = new Set(hiddenData?.wallets || []);
  const curated = CURATED.map((cat) => ({ ...cat, wallets: cat.wallets.filter((w) => !hidden.has(w.addr)) }))
    .filter((cat) => cat.wallets.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <BookOpenIcon size={20} weight="fill" className="text-neon" />
        <h2 className="font-heading font-bold text-xl">Dev Wallet Playbook</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        How to find, vet and track the wallets behind Solana meme launches — plus a starter set of publicly-discussed
        smart-money & KOL wallets you can drop straight onto your <span className="text-[#F0C000] font-semibold">Alpha Devs</span> list to test the radar.
      </p>

      <div className="border border-danger/30 bg-danger/[0.06] p-5 space-y-3">
        <div className="flex items-center gap-2">
          <ShieldWarningIcon size={20} weight="fill" className="text-danger shrink-0" />
          <span className="font-heading font-bold text-sm text-danger">Read before you ape</span>
        </div>
        <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
          <li className="flex gap-2"><span className="text-danger font-bold shrink-0">•</span><span><span className="text-white font-semibold">Not financial advice.</span> Everything here is for education & analysis only. Meme coins are extremely high-risk — most go to zero. Only risk what you can afford to lose.</span></li>
          <li className="flex gap-2"><span className="text-danger font-bold shrink-0">•</span><span><span className="text-white font-semibold">No guarantee of ownership.</span> Names/handles rely on public info + on-chain heuristics and <span className="text-white">cannot be 100% verified</span> unless the person disclosed the wallet themselves.</span></li>
          <li className="flex gap-2"><span className="text-danger font-bold shrink-0">•</span><span><span className="text-white font-semibold">The copy-trading trap.</span> KOLs often wash-trade, get free dev tokens to pump-and-dump, and run undisclosed hedge wallets. Following blindly usually means retail loses. These are research signals, not buy instructions.</span></li>
        </ul>
        <Link to="/disclaimer" data-testid="playbook-disclaimer-link" className="inline-flex items-center gap-1 text-xs text-neon hover:underline">Read the full disclaimer <ArrowSquareOutIcon size={12} /></Link>
      </div>


      {/* How to find a dev wallet */}
      <section className="border border-white/10 bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <MagnifyingGlassIcon size={18} weight="bold" className="text-white" />
          <h3 className="font-heading font-bold text-base">Find a coin's dev wallet</h3>
        </div>
        <ol className="space-y-2">
          {FIND_STEPS.map((s, i) => (
            <li key={i} className="flex gap-3 text-sm text-muted-foreground">
              <span className="font-mono text-[#F0C000] font-bold shrink-0">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Red flags / green signals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="border border-danger/30 bg-danger/[0.04] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <WarningIcon size={18} weight="fill" className="text-danger" />
            <h3 className="font-heading font-bold text-base text-danger">Red flags</h3>
          </div>
          <ul className="space-y-2">
            {RED_FLAGS.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-danger shrink-0">•</span><span>{f}</span></li>
            ))}
          </ul>
        </section>
        <section className="border border-[#C9A227]/30 bg-[#C9A227]/[0.04] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon size={18} weight="fill" className="text-[#F0C000]" />
            <h3 className="font-heading font-bold text-base text-[#F0C000]">Green signals</h3>
          </div>
          <ul className="space-y-2">
            {GREEN_SIGNALS.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-[#F0C000] shrink-0">•</span><span>{f}</span></li>
            ))}
          </ul>
        </section>
      </div>

      {/* The anon → KOL airdrop signal */}
      <section className="border border-white/10 bg-card p-5 space-y-2">
        <h3 className="font-heading font-bold text-base">The anon-dev → KOL airdrop signal</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          A common bootstrap tactic: an <span className="text-white">anonymous deployer</span> launches on Pump.fun and immediately sends a large chunk of supply to a well-known KOL wallet to manufacture attention (this is exactly how <span className="text-white">$ANSEM</span> was seeded — the anon creator sent ~65% straight to Ansem's wallet). When an anon wallet dumps big supply into a KOL, that early transfer can itself be the signal. Track both the anon deployer <span className="text-white">and</span> the receiving KOL wallet.
        </p>
      </section>

      {/* Curated wallets */}
      {curated.map((cat) => (
        <section key={cat.group} className="space-y-3">
          <div className="flex items-center gap-2">
            <StarIcon size={16} weight="fill" className="text-[#F0C000]" />
            <h3 className="font-heading font-bold text-base">{cat.group}</h3>
            <span className="font-mono text-[10px] text-muted-foreground">{cat.wallets.length}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            {cat.wallets.map((w) => (
              <WalletRow key={`${cat.tag}-${w.name}-${w.addr}`} w={w} groupName={cat.group} isAlpha={isAlpha} onAdd={addWallet} />
            ))}
          </div>
        </section>
      ))}

      {/* Tools */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <LinkSimpleIcon size={16} weight="bold" className="text-white" />
          <h3 className="font-heading font-bold text-base">Research tools & links</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {TOOLS.map((t) => (
            <a
              key={t.name}
              data-testid={`playbook-tool-${t.name}`}
              href={t.url}
              target="_blank"
              rel="noreferrer"
              className="group border border-white/10 bg-card p-3.5 hover:border-neon/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-sm group-hover:text-neon transition-colors">{t.name}</span>
                <ArrowSquareOutIcon size={13} className="text-muted-foreground group-hover:text-neon" />
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.note}</p>
            </a>
          ))}
        </div>
      </section>

      <RemovalForm />

      <p className="text-xs text-muted-foreground/70 leading-relaxed border-t border-white/5 pt-4">
        Pro tip: add a batch, then let AlphaApeing tell you which wallets actually fire on clean, early launches vs. noise —
        prune the cold ones and keep the ones that consistently ping you before a coin hits ~500k MCAP.
      </p>
    </div>
  );
};
